import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, 
  Building, 
  Palette, 
  FileText, 
  Settings as SettingsIcon,
  Plus,
  Edit,
  Trash2,
  X
} from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import Button from '../../components/UI/Button';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { getLogoUrl, handleLogoError } from '../../utils/logoUtils';

// --- Roles & Permissions Tab ---
const defaultRoles = ['admin', 'manager', 'user'];
const resources = [
  'dashboard',
  'products', 
  'invoices', 
  'reports', 
  'projects', 
  'customers',
  'suppliers',
  'employees',
  'users', 
  'settings',
  'categories',
  'brands',
  'locations',
  'productTypes',
  'compressorTypes',
  'countries',
  'units'
];
const actions = ['create', 'read', 'update', 'delete'];

const RolesPermissionsTab: React.FC = () => {
  const { user } = useAuth();
  const { refreshPermissions } = usePermissions();
  const [roles, setRoles] = React.useState<any>({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/users?limit=1000');
      const users = res.data.users || [];
      const rolePerms: any = {};
      
      // Define default permissions for each role
      const defaultPermissions = {
        admin: {
          dashboard: { create: true, read: true, update: true, delete: true },
          products: { create: true, read: true, update: true, delete: true },
          invoices: { create: true, read: true, update: true, delete: true },
          reports: { create: true, read: true, update: true, delete: true },
          projects: { create: true, read: true, update: true, delete: true },
          customers: { create: true, read: true, update: true, delete: true },
          suppliers: { create: true, read: true, update: true, delete: true },
          employees: { create: true, read: true, update: true, delete: true },
          users: { create: true, read: true, update: true, delete: true },
          settings: { create: true, read: true, update: true, delete: true },
          categories: { create: true, read: true, update: true, delete: true },
          brands: { create: true, read: true, update: true, delete: true },
          locations: { create: true, read: true, update: true, delete: true },
          productTypes: { create: true, read: true, update: true, delete: true },
          compressorTypes: { create: true, read: true, update: true, delete: true },
          countries: { create: true, read: true, update: true, delete: true },
          units: { create: true, read: true, update: true, delete: true }
        },
        manager: {
          dashboard: { create: false, read: true, update: false, delete: false },
          products: { create: true, read: true, update: true, delete: false },
          invoices: { create: true, read: true, update: true, delete: false },
          reports: { create: true, read: true, update: false, delete: false },
          projects: { create: true, read: true, update: true, delete: false },
          customers: { create: true, read: true, update: true, delete: false },
          suppliers: { create: true, read: true, update: true, delete: false },
          employees: { create: false, read: true, update: false, delete: false },
          users: { create: false, read: true, update: false, delete: false },
          settings: { create: false, read: true, update: false, delete: false },
          categories: { create: true, read: true, update: true, delete: false },
          brands: { create: true, read: true, update: true, delete: false },
          locations: { create: true, read: true, update: true, delete: false },
          productTypes: { create: true, read: true, update: true, delete: false },
          compressorTypes: { create: true, read: true, update: true, delete: false },
          countries: { create: true, read: true, update: true, delete: false },
          units: { create: true, read: true, update: true, delete: false }
        },
        user: {
          dashboard: { create: false, read: true, update: false, delete: false },
          products: { create: false, read: true, update: false, delete: false },
          invoices: { create: false, read: true, update: false, delete: false },
          reports: { create: false, read: true, update: false, delete: false },
          projects: { create: false, read: true, update: false, delete: false },
          customers: { create: false, read: true, update: false, delete: false },
          suppliers: { create: false, read: true, update: false, delete: false },
          employees: { create: false, read: false, update: false, delete: false },
          users: { create: false, read: false, update: false, delete: false },
          settings: { create: false, read: false, update: false, delete: false },
          categories: { create: false, read: true, update: false, delete: false },
          brands: { create: false, read: true, update: false, delete: false },
          locations: { create: false, read: true, update: false, delete: false },
          productTypes: { create: false, read: true, update: false, delete: false },
          compressorTypes: { create: false, read: true, update: false, delete: false },
          countries: { create: false, read: true, update: false, delete: false },
          units: { create: false, read: true, update: false, delete: false }
        }
      };
      
      defaultRoles.forEach(role => {
        const user = users.find((u: any) => u.role === role);
        // Use user permissions if available, otherwise use default permissions
        rolePerms[role] = user && user.permissions ? user.permissions : defaultPermissions[role as keyof typeof defaultPermissions];
      });
      
      console.log('Loaded role permissions:', rolePerms);
      setRoles(rolePerms);
    } catch (e) {
      console.error('Error fetching roles:', e);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchRoles();
  }, []);

  const handleToggle = (role: string, resource: string, action: string) => {
    setRoles((prev: any) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [resource]: {
          ...((prev[role] && prev[role][resource]) || {}),
          [action]: !(prev[role] && prev[role][resource] && prev[role][resource][action])
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      console.log('Starting to save permissions...');
      console.log('Current roles state:', roles);

      // For each role, update all users with that role
      for (const role of defaultRoles) {
        try {
          console.log(`Fetching users for role: ${role}`);
          const res = await apiService.get(`/users?role=${role}&limit=1000`);
          const users = res.data.users || [];
          console.log(`Found ${users.length} users with role ${role}`);
          
          for (const u of users) {
            try {
              console.log(`Updating permissions for user ${u._id} (${u.username || u.email})`);
              console.log(`Sending permissions:`, roles[role]);
              
              // Use the new permissions endpoint
              const response = await apiService.patch(`/users/${u._id}/permissions`, { 
                permissions: roles[role]
              });
              
              console.log(`Successfully updated user ${u._id}:`, response);
              successCount++;
            } catch (userError: any) {
              console.error(`Error updating user ${u._id}:`, userError);
              console.error(`Error details:`, userError.response?.data);
              errorCount++;
            }
          }
        } catch (roleError) {
          console.error(`Error fetching users for role ${role}:`, roleError);
          errorCount++;
        }
      }

      console.log(`Save completed. Success: ${successCount}, Errors: ${errorCount}`);

      if (errorCount === 0) {
        toast.success('Permissions updated successfully');
        // Refresh the data to show the updated permissions
        await fetchRoles();
        // Refresh permissions in the context
        await refreshPermissions();
      } else if (successCount > 0) {
        toast.success(`Updated ${successCount} users successfully. ${errorCount} users failed.`);
        // Refresh the data to show the updated permissions
        await fetchRoles();
        // Refresh permissions in the context
        await refreshPermissions();
      } else {
        toast.error('Failed to update permissions');
      }
    } catch (e) {
      console.error('Error saving permissions:', e);
      toast.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return <div className="text-red-600">Only admin can manage roles and permissions.</div>;
  }
  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Roles & Permissions</h2>
      <DoubleScrollbar>
        <table className="min-w-full border text-xs sm:text-sm">
          <thead>
            <tr>
              <th className="border px-2 py-1">Role</th>
              <th className="border px-2 py-1">Resource</th>
              {actions.map(action => (
                <th key={action} className="border px-2 py-1 capitalize">{action}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {defaultRoles.map(role => (
              resources.map(resource => (
                <tr key={role + resource}>
                  <td className="border px-2 py-1 font-bold">{role}</td>
                  <td className="border px-2 py-1">{resource}</td>
                  {actions.map(action => (
                    <td key={action} className="border px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={!!(roles[role] && roles[role][resource] && roles[role][resource][action])}
                        onChange={() => handleToggle(role, resource, action)}
                        disabled={role === 'admin' && action === 'read'}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </DoubleScrollbar>
      <div className="flex justify-end mt-4">
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <LoadingSpinner size="sm" /> : 'Save Permissions'}
        </Button>
      </div>
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const { settings, updateSettings, updateCompany, updateInvoiceSettings, updateSystemSettings, updateBarcodeSettings, updateEmailSettings, addCustomField, updateCustomField, removeCustomField, loadSettings } = useSettings();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [customFieldModal, setCustomFieldModal] = useState<{
    isOpen: boolean;
    type: 'products' | 'invoices';
    editingField?: any;
  }>({ isOpen: false, type: 'products' });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    type: 'products' | 'invoices';
    fieldName: string;
  }>({ isOpen: false, type: 'products', fieldName: '' });
  const [customFieldForm, setCustomFieldForm] = useState({
    name: '',
    type: 'text' as 'text' | 'number' | 'date' | 'select' | 'textarea',
    required: false,
    options: [] as string[]
  });
  const [formData, setFormData] = useState({
    company: {
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      taxNumber: '',
      currency: 'BDT',
      currencySymbol: '\u09f3'
    },
    invoice: {
      prefix: '',
      suffix: '',
      terms: '',
      footer: '',
      logo: null as File | null
    },
    purchaseInvoice: {
      prefix: '',
      suffix: '',
      terms: '',
      footer: '',
      logo: null as File | null
    },
    deliveryInvoice: {
      prefix: '',
      suffix: '',
      terms: '',
      footer: '',
      logo: null as File | null
    },
    returnInvoice: {
      prefix: '',
      suffix: '',
      terms: '',
      footer: '',
      logo: null as File | null
    },
    theme: {
      primaryColor: '#2563eb',
      secondaryColor: '#64748b',
      fontFamily: 'Times New Roman',
      borderRadius: '8px'
    },
    system: {
      timezone: '',
      dateFormat: '',
      timeFormat: '',
      language: '',
      enableNotifications: false,
      enableAuditLog: false,
      maxFileSize: 0,
      allowedFileTypes: ''
    },
    barcode: {
      format: '',
      width: 0,
      height: 0,
      includeText: false
    },
    email: {
      smtpHost: '',
      smtpPort: '',
      smtpUser: '',
      smtpPass: '',
      smtpSecure: false,
      from: '',
      invoiceSubject: '',
      invoiceBody: ''
    },
    customFields: {
      products: [],
      invoices: []
    }
  });

  useEffect(() => {
    if (settings) { // Always sync with settings when they change
      setFormData({
        company: {
          name: settings.company?.name || '',
          address: settings.company?.address || '',
          phone: settings.company?.phone || '',
          email: settings.company?.email || '',
          website: settings.company?.website || '',
          taxNumber: settings.company?.taxNumber || '',
          currency: settings.invoice?.defaultCurrency || 'BDT',
          currencySymbol: settings.invoice?.defaultCurrencySymbol || '\u09f3'
        },
        invoice: {
          prefix: settings.invoice?.numbering?.prefix || '',
          suffix: settings.invoice?.numbering?.suffix || '',
          terms: '',
          footer: '',
          logo: null
        },
        purchaseInvoice: {
          prefix: settings.purchaseInvoice?.numbering?.prefix || '',
          suffix: settings.purchaseInvoice?.numbering?.suffix || '',
          terms: settings.purchaseInvoice?.template?.footer?.customFooter || '',
          footer: settings.purchaseInvoice?.template?.footer?.customFooter || '',
          logo: null
        },
        deliveryInvoice: {
          prefix: settings.deliveryInvoice?.numbering?.prefix || '',
          suffix: settings.deliveryInvoice?.numbering?.suffix || '',
          terms: settings.deliveryInvoice?.template?.footer?.customFooter || '',
          footer: settings.deliveryInvoice?.template?.footer?.customFooter || '',
          logo: null
        },
        returnInvoice: {
          prefix: settings.returnInvoice?.numbering?.prefix || '',
          suffix: settings.returnInvoice?.numbering?.suffix || '',
          terms: settings.returnInvoice?.template?.footer?.customFooter || '',
          footer: settings.returnInvoice?.template?.footer?.customFooter || '',
          logo: null
        },
        theme: {
          primaryColor: (settings as any)?.theme?.primaryColor || '#2563eb',
          secondaryColor: (settings as any)?.theme?.secondaryColor || '#64748b',
          fontFamily: (settings as any)?.theme?.fontFamily || 'Times New Roman',
          borderRadius: (settings as any)?.theme?.borderRadius || '8px'
        },
        system: {
          timezone: settings.system?.timezone || '',
          dateFormat: settings.system?.dateFormat || '',
          timeFormat: settings.system?.timeFormat || '',
          language: settings.system?.language || '',
          enableNotifications: settings.system?.enableNotifications || false,
          enableAuditLog: settings.system?.enableAuditLog || false,
          maxFileSize: settings.system?.maxFileSize || 0,
          allowedFileTypes: Array.isArray(settings.system?.allowedFileTypes) ? settings.system.allowedFileTypes.join(', ') : ''
        },
        barcode: {
          format: settings.barcode?.format || '',
          width: settings.barcode?.width || 0,
          height: settings.barcode?.height || 0,
          includeText: settings.barcode?.includeText || false
        },
        email: {
          smtpHost: settings.email?.smtp?.host || '',
          smtpPort: settings.email?.smtp?.port?.toString() || '',
          smtpUser: settings.email?.smtp?.auth?.user || '',
          smtpPass: settings.email?.smtp?.auth?.pass || '',
          smtpSecure: settings.email?.smtp?.secure || false,
          from: settings.email?.from || '',
          invoiceSubject: settings.email?.templates?.invoice?.subject || '',
          invoiceBody: settings.email?.templates?.invoice?.body || ''
        },
        customFields: {
          products: (settings.customFields?.products as any) || [],
          invoices: (settings.customFields?.invoices as any) || []
        }
      });
    }
  }, [settings, theme]);

  const handleInputChange = (section: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }));
  };

  const handleCustomFieldInputChange = (field: string, value: any) => {
    setCustomFieldForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddCustomFieldOption = () => {
    const newOption = prompt('Enter option value:');
    if (newOption && newOption.trim()) {
      setCustomFieldForm(prev => ({
        ...prev,
        options: [...prev.options, newOption.trim()]
      }));
    }
  };

  const handleRemoveCustomFieldOption = (index: number) => {
    setCustomFieldForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const openCustomFieldModal = (type: 'products' | 'invoices', field?: any) => {
    setCustomFieldModal({ isOpen: true, type, editingField: field });
    if (field) {
      setCustomFieldForm({
        name: field.name,
        type: field.type,
        required: field.required,
        options: field.options || []
      });
    } else {
      setCustomFieldForm({
        name: '',
        type: 'text',
        required: false,
        options: []
      });
    }
  };

  const closeCustomFieldModal = () => {
    setCustomFieldModal({ isOpen: false, type: 'products' });
    setCustomFieldForm({
      name: '',
      type: 'text',
      required: false,
      options: []
    });
  };

  const handleSaveCustomField = async () => {
    try {
      if (!customFieldForm.name.trim()) {
        toast.error('Field name is required');
        return;
      }

      if (customFieldForm.type === 'select' && customFieldForm.options.length === 0) {
        toast.error('Select fields must have at least one option');
        return;
      }

      setLoading(true);
      
      if (customFieldModal.editingField) {
        await updateCustomField(customFieldModal.type, customFieldModal.editingField.name, customFieldForm);
      } else {
        await addCustomField(customFieldModal.type, customFieldForm);
      }
      
      closeCustomFieldModal();
    } catch (error) {
      toast.error('Failed to save custom field');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomField = async (type: 'products' | 'invoices', fieldName: string) => {
    setDeleteConfirmModal({ isOpen: true, type, fieldName });
  };

  const confirmDeleteCustomField = async () => {
    try {
      setLoading(true);
      await removeCustomField(deleteConfirmModal.type, deleteConfirmModal.fieldName);
      setDeleteConfirmModal({ isOpen: false, type: 'products', fieldName: '' });
    } catch (error) {
      toast.error('Failed to delete custom field');
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteCustomField = () => {
    setDeleteConfirmModal({ isOpen: false, type: 'products', fieldName: '' });
  };

  // New: handle logo upload for each invoice type




  // Helper for file validation
  const validateFile = (file: File) => {
    const maxFileSize = settings?.system?.maxFileSize || 0;
    const allowedFileTypes = settings?.system?.allowedFileTypes || [];
    if (maxFileSize && file.size > maxFileSize) {
      toast.error(`File size exceeds the maximum allowed (${(maxFileSize/1024/1024).toFixed(2)} MB)`);
      return false;
    }
    if (allowedFileTypes.length > 0 && !allowedFileTypes.includes(file.type)) {
      toast.error('File type not allowed.');
      return false;
    }
    return true;
  };

  const handleSave = async (section: string) => {
    try {
      setLoading(true);
      const dataToSave = formData[section as keyof typeof formData];
      if (section === 'company') {
        const { name, address, phone, email, website, taxNumber } = dataToSave as any;
        await updateCompany({ name, address, phone, email, website, taxNumber, registrationNumber: '' });
        // Force immediate form data update for company section
        setFormData(prev => ({
          ...prev,
          company: {
            ...prev.company,
            name,
            address,
            phone,
            email,
            website,
            taxNumber
          }
        }));
      } else if (section === 'purchaseInvoice') {
        const { prefix, suffix } = dataToSave as any;
        await updateInvoiceSettings('purchase', { numbering: { prefix, suffix, startNumber: 1, format: '' } });
        // Force immediate form data update for purchase invoice section
        setFormData(prev => ({
          ...prev,
          purchaseInvoice: {
            ...prev.purchaseInvoice,
            prefix,
            suffix
          }
        }));
      } else if (section === 'deliveryInvoice') {
        const { prefix, suffix } = dataToSave as any;
        await updateInvoiceSettings('delivery', { numbering: { prefix, suffix, startNumber: 1, format: '' } });
        // Force immediate form data update for delivery invoice section
        setFormData(prev => ({
          ...prev,
          deliveryInvoice: {
            ...prev.deliveryInvoice,
            prefix,
            suffix
          }
        }));
      } else if (section === 'returnInvoice') {
        const { prefix, suffix } = dataToSave as any;
        await updateInvoiceSettings('return', { numbering: { prefix, suffix, startNumber: 1, format: '' } });
        // Force immediate form data update for return invoice section
        setFormData(prev => ({
          ...prev,
          returnInvoice: {
            ...prev.returnInvoice,
            prefix,
            suffix
          }
        }));
      } else if (section === 'theme') {
        await updateSettings({ theme: {
          primaryColor: (dataToSave as any).primaryColor,
          secondaryColor: (dataToSave as any).secondaryColor,
          fontFamily: (dataToSave as any).fontFamily,
          borderRadius: (dataToSave as any).borderRadius
        }} as any);
        // Force immediate form data update for theme section
        setFormData(prev => ({
          ...prev,
          theme: {
            ...prev.theme,
            primaryColor: (dataToSave as any).primaryColor,
            secondaryColor: (dataToSave as any).secondaryColor,
            fontFamily: (dataToSave as any).fontFamily,
            borderRadius: (dataToSave as any).borderRadius
          }
        }));
      } else if (section === 'system') {
        const { timezone, dateFormat, timeFormat, language, enableNotifications, enableAuditLog, maxFileSize, allowedFileTypes } = dataToSave as any;
        await updateSystemSettings({
          timezone,
          dateFormat,
          timeFormat,
          language,
          enableNotifications,
          enableAuditLog,
          maxFileSize,
          allowedFileTypes: typeof allowedFileTypes === 'string' ? allowedFileTypes.split(',').map((s: string) => s.trim()) : []
        });
        // Force immediate form data update for system section
        setFormData(prev => ({
          ...prev,
          system: {
            ...prev.system,
            timezone,
            dateFormat,
            timeFormat,
            language,
            enableNotifications,
            enableAuditLog,
            maxFileSize,
            allowedFileTypes: typeof allowedFileTypes === 'string' ? allowedFileTypes : ''
          }
        }));
      } else if (section === 'barcode') {
        await updateBarcodeSettings(formData.barcode);
        // Force immediate form data update for barcode section
        setFormData(prev => ({
          ...prev,
          barcode: {
            ...prev.barcode,
            ...formData.barcode
          }
        }));
      } else if (section === 'email') {
        // Validate required email fields
        if (!formData.email.smtpHost || !formData.email.smtpPort || !formData.email.smtpUser || !formData.email.smtpPass || !formData.email.from) {
          toast.error('Please fill in all required email settings fields');
          return;
        }

        // Format email data to match backend structure
        const emailData = {
          smtp: {
            host: formData.email.smtpHost.trim(),
            port: parseInt(formData.email.smtpPort),
            secure: formData.email.smtpSecure,
            auth: {
              user: formData.email.smtpUser.trim(),
              pass: formData.email.smtpPass.trim()
            }
          },
          from: formData.email.from.trim(),
          templates: {
            invoice: {
              subject: formData.email.invoiceSubject || 'Invoice from {company_name}',
              body: formData.email.invoiceBody || 'Please find attached invoice for your reference.'
            }
          }
        };

        // Additional validation
        if (isNaN(emailData.smtp.port) || emailData.smtp.port < 1 || emailData.smtp.port > 65535) {
          toast.error('SMTP port must be a valid number between 1 and 65535');
          return;
        }

        await updateEmailSettings(emailData);
        // Force immediate form data update for email section
        setFormData(prev => ({
          ...prev,
          email: {
            ...prev.email,
            ...formData.email
          }
        }));
      }
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully`);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'company', name: 'Company Info', icon: <Building className="w-4 h-4" /> },
    { id: 'invoice', name: 'Invoice Settings', icon: <FileText className="w-4 h-4" /> },
    { id: 'theme', name: 'Theme & UI', icon: <Palette className="w-4 h-4" /> },
    { id: 'system', name: 'System Settings', icon: <SettingsIcon className="w-4 h-4" /> },
    { id: 'barcode', name: 'Barcode Settings', icon: <SettingsIcon className="w-4 h-4" /> },
    { id: 'email', name: 'Email Settings', icon: <SettingsIcon className="w-4 h-4" /> },
    { id: 'customFields', name: 'Custom Fields', icon: <SettingsIcon className="w-4 h-4" /> },
    { id: 'roles', name: 'Roles & Permissions', icon: <SettingsIcon className="w-4 h-4" /> }
  ];

  return (
    <>
      <style>{`
        .settings-page input::placeholder,
        .settings-page textarea::placeholder {
          background: transparent !important;
        }
        .dark .settings-page input::placeholder,
        .dark .settings-page textarea::placeholder {
          background: transparent !important;
        }
      `}</style>
      <div className="settings-page p-6 space-y-6 bg-[var(--color-background)]" style={{ color: 'var(--color-text)' } as unknown as React.CSSProperties}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[var(--color-text)] font-['Times_New_Roman']">Settings</h1>
        <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
          Configure your system settings and preferences
        </p>
      </div>
      {/* Tabs */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-md">
        <div className="border-b border-[var(--color-border)] overflow-x-auto">
          <nav className="flex space-x-4 sm:space-x-8 px-2 sm:px-6 whitespace-nowrap">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-3 sm:py-4 px-1 border-b-2 font-medium text-sm font-['Times_New_Roman'] ${
                  activeTab === tab.id
                    ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                    : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]'
                }`}
              >
                {tab.icon}
                <span className="ml-2">{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
        <div className="p-2 sm:p-6">
          {/* Company Information */}
          {activeTab === 'company' && (
            <motion.div initial={{ opacity: 0 } as any} animate={{ opacity: 1 } as any} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.company.name}
                    onChange={(e) => handleInputChange('company', 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                    placeholder="The Aircons Ltd."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Company Logo
                  </label>
                  <div className="flex items-center space-x-4">
                    <img 
                      src={settings?.company?.logo ? `${process.env.REACT_APP_API_URL || 'https://aircons-inventory-backend.onrender.com'}${settings.company.logo}` : '/The Aircons Ltd. Logo Org.png'} 
                      alt="Company Logo" 
                      className="h-16 w-auto" 
                      onError={(e) => {
                        console.error('Failed to load company logo');
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      Fixed company logo (The Aircons Ltd.)
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={formData.company.phone}
                    onChange={(e) => handleInputChange('company', 'phone', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                    placeholder="+880 1234-567890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.company.email}
                    onChange={(e) => handleInputChange('company', 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                    placeholder="support@theaircons.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.company.website}
                    onChange={(e) => handleInputChange('company', 'website', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                    placeholder="https://www.theaircons.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Address
                  </label>
                  <textarea
                    value={formData.company.address}
                    onChange={(e) => handleInputChange('company', 'address', e.target.value)}
                    rows={3}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                    placeholder="Enter company address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Tax Number
                  </label>
                  <input
                    type="text"
                    value={formData.company.taxNumber}
                    onChange={(e) => handleInputChange('company', 'taxNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                    placeholder="Tax registration number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Currency
                  </label>
                  <select
                    value={formData.company.currency}
                    onChange={(e) => handleInputChange('company', 'currency', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                  >
                    <option value="BDT">৳ Bangladeshi Taka</option>
                    <option value="EUR">Euro (EUR)</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave('company')}
                  disabled={loading}
                  icon={<Save className="w-4 h-4 mr-2" />}
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Save Company Settings'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Invoice Settings */}
          {activeTab === 'invoice' && (
            <motion.div initial={{ opacity: 0 } as any} animate={{ opacity: 1 } as any} className="space-y-10">
                {/* Purchase Invoice Settings */}
                <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-bold mb-4 text-[var(--color-text)] font-['Times_New_Roman']">Purchase Invoice Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Prefix</label>
                      <input type="text" value={formData.purchaseInvoice?.prefix || ''} onChange={e => handleInputChange('purchaseInvoice', 'prefix', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="PINV-" />
                </div>
                <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Suffix</label>
                      <input type="text" value={formData.purchaseInvoice?.suffix || ''} onChange={e => handleInputChange('purchaseInvoice', 'suffix', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="-2024" />
                </div>
                <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Logo</label>
                      <div className="flex items-center space-x-4">
                        <img 
                          src="file:///Volumes/Ext%20SSD/GDrive/Program/aircons-inventory-system/client/build/The%20Aircons%20Ltd.%20Logo%20Org.png" 
                          alt="Purchase Invoice Logo" 
                          className="h-12 w-auto" 
                          onError={(e) => {
                            console.error('Failed to load purchase invoice logo');
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className="text-sm text-[var(--color-text-secondary)]">
                          Fixed company logo (The Aircons Ltd.)
                        </span>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Terms & Conditions</label>
                      <textarea value={formData.purchaseInvoice?.terms || ''} onChange={e => handleInputChange('purchaseInvoice', 'terms', e.target.value)} rows={3} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Enter terms and conditions for purchase invoices" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Footer</label>
                      <textarea value={formData.purchaseInvoice?.footer || ''} onChange={e => handleInputChange('purchaseInvoice', 'footer', e.target.value)} rows={2} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Enter footer text for purchase invoices" />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      variant="primary"
                      onClick={() => handleSave('purchaseInvoice')}
                      disabled={loading}
                      icon={<Save className="w-4 h-4 mr-2" />}
                    >
                      {loading ? <LoadingSpinner size="sm" /> : 'Save Purchase Invoice Settings'}
                    </Button>
                  </div>
                </div>
                {/* Delivery Invoice Settings */}
                <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-bold mb-4 text-[var(--color-text)] font-['Times_New_Roman']">Delivery Invoice Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Prefix</label>
                      <input type="text" value={formData.deliveryInvoice?.prefix || ''} onChange={e => handleInputChange('deliveryInvoice', 'prefix', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="DINV-" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Suffix</label>
                      <input type="text" value={formData.deliveryInvoice?.suffix || ''} onChange={e => handleInputChange('deliveryInvoice', 'suffix', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="-2024" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Logo</label>
                      <div className="flex items-center space-x-4">
                        <img 
                          src="file:///Volumes/Ext%20SSD/GDrive/Program/aircons-inventory-system/client/build/The%20Aircons%20Ltd.%20Logo%20Org.png" 
                          alt="Delivery Invoice Logo" 
                          className="h-12 w-auto" 
                          onError={(e) => {
                            console.error('Failed to load delivery invoice logo');
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className="text-sm text-[var(--color-text-secondary)]">
                          Fixed company logo (The Aircons Ltd.)
                        </span>
                      </div>
                  </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Terms & Conditions</label>
                      <textarea value={formData.deliveryInvoice?.terms || ''} onChange={e => handleInputChange('deliveryInvoice', 'terms', e.target.value)} rows={3} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Enter terms and conditions for delivery invoices" />
                </div>
                <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Footer</label>
                      <textarea value={formData.deliveryInvoice?.footer || ''} onChange={e => handleInputChange('deliveryInvoice', 'footer', e.target.value)} rows={2} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Enter footer text for delivery invoices" />
                </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      variant="primary"
                      onClick={() => handleSave('deliveryInvoice')}
                      disabled={loading}
                      icon={<Save className="w-4 h-4 mr-2" />}
                    >
                      {loading ? <LoadingSpinner size="sm" /> : 'Save Delivery Invoice Settings'}
                    </Button>
                  </div>
                </div>
                {/* Return Invoice Settings */}
                <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-bold mb-4 text-[var(--color-text)] font-['Times_New_Roman']">Return Invoice Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Prefix</label>
                      <input type="text" value={formData.returnInvoice?.prefix || ''} onChange={e => handleInputChange('returnInvoice', 'prefix', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="RINV-" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Suffix</label>
                      <input type="text" value={formData.returnInvoice?.suffix || ''} onChange={e => handleInputChange('returnInvoice', 'suffix', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="-2024" />
                    </div>
                <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Logo</label>
                      <div className="flex items-center space-x-4">
                        <img 
                          src="file:///Volumes/Ext%20SSD/GDrive/Program/aircons-inventory-system/client/build/The%20Aircons%20Ltd.%20Logo%20Org.png" 
                          alt="Return Invoice Logo" 
                          className="h-12 w-auto" 
                          onError={(e) => {
                            console.error('Failed to load return invoice logo');
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className="text-sm text-[var(--color-text-secondary)]">
                          Fixed company logo (The Aircons Ltd.)
                        </span>
                      </div>
                </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Terms & Conditions</label>
                      <textarea value={formData.returnInvoice?.terms || ''} onChange={e => handleInputChange('returnInvoice', 'terms', e.target.value)} rows={3} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Enter terms and conditions for return invoices" />
              </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">Footer</label>
                      <textarea value={formData.returnInvoice?.footer || ''} onChange={e => handleInputChange('returnInvoice', 'footer', e.target.value)} rows={2} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Enter footer text for return invoices" />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      variant="primary"
                      onClick={() => handleSave('returnInvoice')}
                  disabled={loading}
                      icon={<Save className="w-4 h-4 mr-2" />}
                    >
                      {loading ? <LoadingSpinner size="sm" /> : 'Save Return Invoice Settings'}
                    </Button>
                  </div>
              </div>
            </motion.div>
          )}

          {/* Theme Settings */}
          {activeTab === 'theme' && (
            <motion.div initial={{ opacity: 0 } as any} animate={{ opacity: 1 } as any} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Primary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.theme.primaryColor}
                      onChange={(e) => handleInputChange('theme', 'primaryColor', e.target.value)}
                      className="w-12 h-10 border border-[var(--color-border)] rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.theme.primaryColor}
                      onChange={(e) => handleInputChange('theme', 'primaryColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Secondary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.theme.secondaryColor}
                      onChange={(e) => handleInputChange('theme', 'secondaryColor', e.target.value)}
                      className="w-12 h-10 border border-[var(--color-border)] rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.theme.secondaryColor}
                      onChange={(e) => handleInputChange('theme', 'secondaryColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Font Family
                  </label>
                  <select
                    value={formData.theme.fontFamily}
                    onChange={(e) => handleInputChange('theme', 'fontFamily', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                  >
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                    Border Radius
                  </label>
                  <select
                    value={formData.theme.borderRadius}
                    onChange={(e) => handleInputChange('theme', 'borderRadius', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                  >
                    <option value="0px">None</option>
                    <option value="4px">Small (4px)</option>
                    <option value="8px">Medium (8px)</option>
                    <option value="12px">Large (12px)</option>
                    <option value="16px">Extra Large (16px)</option>
                  </select>
                </div>
              </div>

              {/* Theme Preview */}
              <div className="bg-[var(--color-surface)] rounded-lg p-6">
                <h4 className="text-lg font-semibold text-[var(--color-text)] mb-4 font-['Times_New_Roman']">Theme Preview</h4>
                <div 
                  className="p-4 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    color: formData.theme.secondaryColor,
                    fontFamily: formData.theme.fontFamily,
                    borderRadius: formData.theme.borderRadius
                  } as unknown as React.CSSProperties}
                >
                  <h3 style={{ color: formData.theme.primaryColor } as unknown as React.CSSProperties} className="text-xl font-bold mb-2 font-['Times_New_Roman']">
                    Sample Heading
                  </h3>
                  <p className="mb-2 font-['Times_New_Roman']">
                    This is a sample paragraph showing how your theme will look with the selected colors and fonts.
                  </p>
                  <button 
                    className="px-4 py-2 rounded-lg text-[var(--color-on-primary)] font-['Times_New_Roman']"
                    style={{ backgroundColor: formData.theme.primaryColor } as unknown as React.CSSProperties}
                  >
                    Sample Button
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave('theme')}
                  disabled={loading}
                  icon={<Save className="w-4 h-4 mr-2" />}
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Save Theme Settings'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Barcode Settings */}
          {activeTab === 'barcode' && (
            <motion.div initial={{ opacity: 0 } as any} animate={{ opacity: 1 } as any} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Barcode Format</label>
                    <input type="text" value={formData.barcode.format} onChange={e => handleInputChange('barcode', 'format', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Code128" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Width (px)</label>
                    <input type="number" value={formData.barcode.width} onChange={e => handleInputChange('barcode', 'width', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Height (px)</label>
                    <input type="number" value={formData.barcode.height} onChange={e => handleInputChange('barcode', 'height', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="100" />
                </div>
                <div className="flex items-center">
                  <input type="checkbox" checked={formData.barcode.includeText} onChange={e => handleInputChange('barcode', 'includeText', e.target.checked)} className="mr-2" />
                  <label className="text-sm font-medium text-[var(--color-text-secondary)]">Include Text</label>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave('barcode')}
                  disabled={loading}
                  icon={<Save className="w-4 h-4 mr-2" />}
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Save Barcode Settings'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Email Settings */}
          {activeTab === 'email' && (
            <motion.div initial={{ opacity: 0 } as any} animate={{ opacity: 1 } as any} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">SMTP Host</label>
                  <input type="text" value={formData.email.smtpHost} onChange={e => handleInputChange('email', 'smtpHost', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="smtp.example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">SMTP Port</label>
                  <input type="text" value={formData.email.smtpPort} onChange={e => handleInputChange('email', 'smtpPort', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="465 or 587" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">SMTP Username</label>
                  <input type="text" value={formData.email.smtpUser} onChange={e => handleInputChange('email', 'smtpUser', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="user@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">SMTP Password</label>
                  <input type="password" value={formData.email.smtpPass} onChange={e => handleInputChange('email', 'smtpPass', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Password" />
                </div>
                <div className="flex items-center mt-2">
                  <input type="checkbox" checked={formData.email.smtpSecure} onChange={e => handleInputChange('email', 'smtpSecure', e.target.checked)} className="mr-2" id="smtpSecure" />
                  <label htmlFor="smtpSecure" className="text-sm font-medium text-[var(--color-text-secondary)]">Secure (SSL/TLS)</label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">From Email Address</label>
                  <input type="email" value={formData.email.from} onChange={e => handleInputChange('email', 'from', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="noreply@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Default Invoice Email Subject</label>
                  <input type="text" value={formData.email.invoiceSubject} onChange={e => handleInputChange('email', 'invoiceSubject', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Your Invoice from The Aircons Ltd." />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Default Invoice Email Body</label>
                  <textarea value={formData.email.invoiceBody} onChange={e => handleInputChange('email', 'invoiceBody', e.target.value)} rows={4} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Please find your invoice attached." />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave('email')}
                  disabled={loading}
                  icon={<Save className="w-4 h-4 mr-2" />}
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Save Email Settings'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Custom Fields */}
          {activeTab === 'customFields' && (
            <motion.div initial={{ opacity: 0 } as any} animate={{ opacity: 1 } as any} className="space-y-6">
              {/* Product Custom Fields */}
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-[var(--color-text)]">Product Custom Fields</h4>
                  <Button
                    variant="primary"
                    onClick={() => openCustomFieldModal('products')}
                    icon={<Plus className="w-5 h-5" />}
                  >
                    Add Custom Field
                  </Button>
                </div>
                
                {formData.customFields.products.length === 0 ? (
                  <div className="text-center py-8 text-[var(--color-text-secondary)]">
                    <p>No custom fields defined for products</p>
                    <p className="text-sm">Click "Add Field" to create your first custom field</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.customFields.products.map((field: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-[var(--color-border)] rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <span className="font-medium">{field.name}</span>
                            <span className="px-2 py-1 text-xs bg-[var(--color-border-light)] rounded">{field.type}</span>
                            {field.required && <span className="px-2 py-1 text-xs bg-[var(--color-error-light)] text-[var(--color-error)] rounded">Required</span>}
                          </div>
                          {field.options && field.options.length > 0 && (
                            <div className="mt-1 text-sm text-[var(--color-text-secondary)]">
                              Options: {field.options.join(', ')}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openCustomFieldModal('products', field)}
                            className="p-1 text-[var(--color-primary)] hover:text-[var(--color-primary-dark)]"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCustomField('products', field.name)}
                            className="p-1 text-[var(--color-error)] hover:text-[var(--color-error-dark)]"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invoice Custom Fields */}
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-[var(--color-text)]">Invoice Custom Fields</h4>
                  <Button
                    variant="primary"
                    onClick={() => openCustomFieldModal('invoices')}
                    icon={<Plus className="w-5 h-5" />}
                  >
                    Add Custom Field
                  </Button>
                </div>
                
                {formData.customFields.invoices.length === 0 ? (
                  <div className="text-center py-8 text-[var(--color-text-secondary)]">
                    <p>No custom fields defined for invoices</p>
                    <p className="text-sm">Click "Add Field" to create your first custom field</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.customFields.invoices.map((field: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-[var(--color-border)] rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <span className="font-medium">{field.name}</span>
                            <span className="px-2 py-1 text-xs bg-[var(--color-border-light)] rounded">{field.type}</span>
                            {field.required && <span className="px-2 py-1 text-xs bg-[var(--color-error-light)] text-[var(--color-error)] rounded">Required</span>}
                          </div>
                          {field.options && field.options.length > 0 && (
                            <div className="mt-1 text-sm text-[var(--color-text-secondary)]">
                              Options: {field.options.join(', ')}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openCustomFieldModal('invoices', field)}
                            className="p-1 text-[var(--color-primary)] hover:text-[var(--color-primary-dark)]"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCustomField('invoices', field.name)}
                            className="p-1 text-[var(--color-error)] hover:text-[var(--color-error-dark)]"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Custom Field Modal */}
              {customFieldModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-[var(--color-surface)] rounded-lg p-6 w-full max-w-md mx-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">
                        {customFieldModal.editingField ? 'Edit' : 'Add'} Custom Field
                    </h3>
                      <button
                        onClick={closeCustomFieldModal}
                        className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Field Name</label>
                        <input
                          type="text"
                          value={customFieldForm.name}
                          onChange={(e) => handleCustomFieldInputChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                          placeholder="Enter field name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Field Type</label>
                        <select
                          value={customFieldForm.type}
                          onChange={(e) => handleCustomFieldInputChange('type', e.target.value)}
                            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                        >
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="select">Select (Dropdown)</option>
                          <option value="textarea">Text Area</option>
                        </select>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={customFieldForm.required}
                          onChange={(e) => handleCustomFieldInputChange('required', e.target.checked)}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-[var(--color-text-secondary)]">Required Field</label>
                      </div>

                      {customFieldForm.type === 'select' && (
                        <div>
                          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Options</label>
                          <div className="space-y-2">
                            {customFieldForm.options.map((option, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={option}
                                  onChange={(e) => {
                                    const newOptions = [...customFieldForm.options];
                                    newOptions[index] = e.target.value;
                                    handleCustomFieldInputChange('options', newOptions);
                                  }}
                                    className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                                  placeholder="Option value"
                                />
                                <button
                                  onClick={() => handleRemoveCustomFieldOption(index)}
                                  className="p-2 text-[var(--color-error)] hover:text-[var(--color-error-dark)]"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={handleAddCustomFieldOption}
                              className="flex items-center px-3 py-2 text-[var(--color-primary)] hover:text-[var(--color-primary-dark)]"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Option
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        onClick={closeCustomFieldModal}
                        className="px-4 py-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
                      >
                        Cancel
                      </button>
                      <Button
                        variant="primary"
                        onClick={handleSaveCustomField}
                        disabled={loading}
                        icon={loading ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4 mr-2" />}
                      >
                        {loading ? <LoadingSpinner size="sm" /> : 'Add Field'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Confirmation Modal */}
              {deleteConfirmModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-[var(--color-surface)] rounded-lg p-6 w-full max-w-md mx-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-[var(--color-error)]">Confirm Deletion</h3>
                      <button
                        onClick={cancelDeleteCustomField}
                        className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="mb-6">
                      <p className="text-[var(--color-text-secondary)]">
                        Are you sure you want to delete the field <span className="font-semibold">"{deleteConfirmModal.fieldName}"</span>?
                      </p>
                      <p className="text-sm text-[var(--color-text-secondary)] mt-2">
                        This action cannot be undone.
                      </p>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={cancelDeleteCustomField}
                        className="px-4 py-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
                      >
                        Cancel
                      </button>
                      <Button
                        variant="danger"
                        onClick={confirmDeleteCustomField}
                        disabled={loading}
                        icon={loading ? <LoadingSpinner size="sm" /> : <Trash2 className="w-4 h-4 mr-2" />}
                      >
                        {loading ? <LoadingSpinner size="sm" /> : 'Delete Field'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Email Settings */}
          {activeTab === 'email' && (
            <motion.div initial={{ opacity: 0 } as any} animate={{ opacity: 1 } as any} className="space-y-6">
              <div className="bg-[var(--color-surface)] rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-bold mb-6 text-[var(--color-text)] font-['Times_New_Roman']">Email Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                      SMTP Host
                    </label>
                    <input
                      type="text"
                      value={formData.email.smtpHost}
                      onChange={(e) => handleInputChange('email', 'smtpHost', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                      placeholder="smtp.gmail.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                      SMTP Port
                    </label>
                    <input
                      type="number"
                      value={formData.email.smtpPort}
                      onChange={(e) => handleInputChange('email', 'smtpPort', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                      placeholder="465"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                      SMTP Username
                    </label>
                    <input
                      type="email"
                      value={formData.email.smtpUser}
                      onChange={(e) => handleInputChange('email', 'smtpUser', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                      placeholder="scm.theaircons@gmail.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                      SMTP Password
                    </label>
                    <input
                      type="password"
                      value={formData.email.smtpPass}
                      onChange={(e) => handleInputChange('email', 'smtpPass', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                      placeholder="App password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                      From Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email.from}
                      onChange={(e) => handleInputChange('email', 'from', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                      placeholder="scm.theaircons@gmail.com"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.email.smtpSecure}
                      onChange={(e) => handleInputChange('email', 'smtpSecure', e.target.checked)}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                      Use SSL/TLS (recommended for port 465)
                    </label>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-lg font-semibold mb-4 text-[var(--color-text)] font-['Times_New_Roman']">Email Templates</h4>
                  
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                        Invoice Email Subject
                      </label>
                      <input
                        type="text"
                        value={formData.email.invoiceSubject}
                        onChange={(e) => handleInputChange('email', 'invoiceSubject', e.target.value)}
                        className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                        placeholder="Invoice from {company_name}"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2 font-['Times_New_Roman']">
                        Invoice Email Body
                      </label>
                      <textarea
                        value={formData.email.invoiceBody}
                        onChange={(e) => handleInputChange('email', 'invoiceBody', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]"
                        placeholder="Please find attached invoice for your reference."
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-8">
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Pre-populate with Gmail settings
                      handleInputChange('email', 'smtpHost', 'smtp.gmail.com');
                      handleInputChange('email', 'smtpPort', '465');
                      handleInputChange('email', 'smtpUser', 'scm.theaircons@gmail.com');
                      handleInputChange('email', 'smtpPass', 'dgwveenymynlzcpt');
                      handleInputChange('email', 'from', 'scm.theaircons@gmail.com');
                      handleInputChange('email', 'smtpSecure', true);
                      handleInputChange('email', 'invoiceSubject', 'Invoice from {company_name}');
                      handleInputChange('email', 'invoiceBody', 'Please find attached invoice for your reference.');
                      toast.success('Gmail settings pre-filled! Please save the settings.');
                    }}
                    disabled={loading}
                  >
                    Use Gmail Settings
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      try {
                        // Validate required fields
                        if (!formData.email.smtpHost || !formData.email.smtpPort || !formData.email.smtpUser || !formData.email.smtpPass || !formData.email.from) {
                          toast.error('Please fill in all required email settings before testing.');
                          return;
                        }
                        
                        // First save the email settings
                        await handleSave('email');
                        
                        // Then send test email
                        const response = await apiService.post('/email/test', {
                          to: formData.email.from || 'scm.theaircons@gmail.com'
                        });
                        toast.success('Test email sent successfully!');
                      } catch (error: any) {
                        console.error('Test email error:', error);
                        const errorMessage = error.response?.data?.error || error.response?.data?.details || 'Failed to send test email. Please check your configuration.';
                        toast.error(errorMessage);
                      }
                    }}
                    disabled={loading}
                  >
                    Send Test Email
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleSave('email')}
                    disabled={loading}
                    icon={<Save className="w-4 h-4 mr-2" />}
                  >
                    {loading ? <LoadingSpinner size="sm" /> : 'Save Email Settings'}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* System Settings (Editable) */}
          {activeTab === 'system' && (
            <motion.div initial={{ opacity: 0 } as any} animate={{ opacity: 1 } as any} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Timezone</label>
                    <input type="text" value={formData.system.timezone} onChange={e => handleInputChange('system', 'timezone', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="Asia/Dhaka" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Date Format</label>
                    <input type="text" value={formData.system.dateFormat} onChange={e => handleInputChange('system', 'dateFormat', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="DD/MM/YYYY" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Time Format</label>
                  <input type="text" value={formData.system.timeFormat} onChange={e => handleInputChange('system', 'timeFormat', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="HH:mm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Language</label>
                  <input type="text" value={formData.system.language} onChange={e => handleInputChange('system', 'language', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="en" />
                </div>
                <div className="flex items-center">
                  <input type="checkbox" checked={formData.system.enableNotifications} onChange={e => handleInputChange('system', 'enableNotifications', e.target.checked)} className="mr-2" />
                  <label className="text-sm font-medium text-[var(--color-text-secondary)]">Enable Notifications</label>
                  </div>
                <div className="flex items-center">
                  <input type="checkbox" checked={formData.system.enableAuditLog} onChange={e => handleInputChange('system', 'enableAuditLog', e.target.checked)} className="mr-2" />
                  <label className="text-sm font-medium text-[var(--color-text-secondary)]">Enable Audit Log</label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Max File Size (MB)</label>
                    <input type="number" value={formData.system.maxFileSize} onChange={e => handleInputChange('system', 'maxFileSize', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="5" />
                  </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Allowed File Types (comma separated)</label>
                  <input type="text" value={formData.system.allowedFileTypes} onChange={e => handleInputChange('system', 'allowedFileTypes', e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-transparent focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent placeholder-[var(--color-text-secondary)] dark:placeholder-[var(--color-text)]" placeholder="jpg,png,pdf" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave('system')}
                  disabled={loading}
                  icon={<Save className="w-4 h-4 mr-2" />}
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Save System Settings'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Roles & Permissions */}
          {activeTab === 'roles' && (
            <RolesPermissionsTab />
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default SettingsPage; 